import { useMemo } from 'react';

import type { DataProvider, FormValues } from '../../../shared/types';
import type { InitialDataForDependencies } from '../utils/dataProviderUtils';

import { formatDateDependencyValue } from '../utils/dataProviderUtils';

/**
 * Tracks dependency values for change detection (cascading + data provider reloads).
 * Contextual keys (requestor / requestee / loggedInUser, dotted forms) use entity `id`
 * from initialData so LiveForm reloads after the entity/load API populates Redux.
 */
// eslint-disable-next-line import/prefer-default-export
export const useDependencyValues = (
  dataProvider: DataProvider,
  formValues: FormValues,
  initialData?: InitialDataForDependencies
): string => {
  const dependencyKeys = Object.keys(dataProvider.dependencies || {});
  const dependencyKeysString = dependencyKeys.join(',');

  const requestorId = initialData?.requestor?.id;
  const requesteeId = initialData?.requestee?.id;
  const loggedInUserId = initialData?.loggedInUser?.id;

  const dependencyValues = useMemo(() => {
    const values: Record<string, unknown> = {};

    dependencyKeys.forEach((key) => {
      const trimmed = key.trim();
      const dotIndex = trimmed.indexOf('.');
      const root = dotIndex === -1 ? trimmed : trimmed.slice(0, dotIndex);
      const rootLower = root.toLowerCase();

      if (rootLower === 'requestor') {
        values[key] = requestorId ?? null;
      } else if (rootLower === 'requestee') {
        values[key] = requesteeId ?? null;
      } else if (rootLower === 'loggedinuser') {
        values[key] = loggedInUserId ?? null;
      } else {
        const raw = formValues[key];

        // Serialize Date as formatted string so JSON.stringify produces a stable,
        // meaningful value instead of an ISO string or null.
        values[key] =
          raw instanceof Date && !Number.isNaN(raw.getTime())
            ? formatDateDependencyValue(raw)
            : raw;
      }
    });

    return values;
    // dependencyKeysString ensures recalculation when dependency structure changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dependencyKeysString,
    formValues,
    requestorId,
    requesteeId,
    loggedInUserId,
  ]);

  return JSON.stringify(dependencyValues);
};
